/**
 * Pure geometry helpers for **moving** annotations (ADR-034 — geometry move,
 * 2026-06-11). Translate a whole shape, translate one line vertex, and
 * pixel-hit-test line vertices. No map / no GL — CI-testable; the drag
 * interaction (:file:`@/map/use-annotation-drag.ts`) composes these.
 *
 * Per the architect's model: marker / circle / polygon move as a unit
 * (:func:`translateGeometry` — for a circle the geometry IS the centre point, so
 * the centre moves and `radius_m` is unchanged); a line moves as a unit when its
 * body is grabbed, or one vertex moves when a vertex is grabbed
 * (:func:`translateLineVertex`). Deltas are in lon/lat degrees.
 *
 * Cross-references:
 *   - ADR-034 (D7 — geometry move, un-deferred 2026-06-11); feature spec §8.
 */
import type { Annotation } from "@/annotations/use-annotations";

/** The annotation geometry shape (a loose GeoJSON `{type, coordinates}` whose
 *  coordinates are the kind-polymorphic union). */
type AnnotationGeometry = Annotation["geometry"];

/** A screen-pixel point (the hook projects lon/lat → pixels for hit-testing). */
export interface PixelPoint {
  readonly x: number;
  readonly y: number;
}

/** Translate a position `[lon, lat]` by `(dLng, dLat)` degrees. */
export function translatePosition(pos: readonly number[], dLng: number, dLat: number): number[] {
  return [pos[0] + dLng, pos[1] + dLat];
}

/**
 * Translate a whole geometry (every coordinate) by `(dLng, dLat)`. Point /
 * LineString / Polygon. For a **circle** the geometry is the centre `Point`, so
 * this moves the centre (radius unchanged).
 */
export function translateGeometry(
  geometry: AnnotationGeometry,
  dLng: number,
  dLat: number,
): AnnotationGeometry {
  if (geometry.type === "Point") {
    return {
      ...geometry,
      coordinates: translatePosition(geometry.coordinates as number[], dLng, dLat),
    };
  }
  if (geometry.type === "LineString") {
    return {
      ...geometry,
      coordinates: (geometry.coordinates as number[][]).map((p) =>
        translatePosition(p, dLng, dLat),
      ),
    };
  }
  if (geometry.type === "Polygon") {
    return {
      ...geometry,
      coordinates: (geometry.coordinates as number[][][]).map((ring) =>
        ring.map((p) => translatePosition(p, dLng, dLat)),
      ),
    };
  }
  return geometry;
}

/**
 * Translate ONE vertex of a LineString by `(dLng, dLat)` (the others fixed) —
 * the line-end / line-vertex drag. A non-LineString or out-of-range index is
 * returned unchanged.
 */
export function translateLineVertex(
  geometry: AnnotationGeometry,
  index: number,
  dLng: number,
  dLat: number,
): AnnotationGeometry {
  if (geometry.type !== "LineString") {
    return geometry;
  }
  const coords = geometry.coordinates as number[][];
  if (index < 0 || index >= coords.length) {
    return geometry;
  }
  return {
    ...geometry,
    coordinates: coords.map((p, i) => (i === index ? translatePosition(p, dLng, dLat) : p)),
  };
}

/**
 * The index of the nearest vertex within `thresholdPx` of `cursorPx`, or `null`
 * if none is close enough (→ a body drag). `vertexPx` are the screen-projected
 * vertices (the hook does the projection). Ties resolve to the lower index.
 */
export function nearestVertexIndex(
  vertexPx: readonly PixelPoint[],
  cursorPx: PixelPoint,
  thresholdPx: number,
): number | null {
  let best: number | null = null;
  let bestDist = thresholdPx;
  vertexPx.forEach((v, i) => {
    const d = Math.hypot(v.x - cursorPx.x, v.y - cursorPx.y);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  });
  return best;
}

/* ---- Reshape operations (REQ-UI-136..138; the Edit tool) -------------
 *
 * These are the geometry half of the Edit tool. Each is a pure
 * geometry-in / geometry-out transform so the interaction hook stays a thin
 * layer of pointer bookkeeping over CI-tested math.
 *
 * The recurring hazard across all three is the GeoJSON polygon ring: its first
 * position is repeated last, and every operation has to keep that invariant or
 * the shape tears open along its closing side. Each has a test pinning it. */

/** The unique corners of a polygon ring — the closing repeat dropped. */
function ringCorners(geometry: AnnotationGeometry): number[][] | null {
  if (geometry.type !== "Polygon") {
    return null;
  }
  const ring = (geometry.coordinates as number[][][])[0];
  return ring.slice(0, -1);
}

/** Rebuild a Polygon geometry from its unique corners, re-closing the ring. */
function polygonFromCorners(
  geometry: AnnotationGeometry,
  corners: readonly number[][],
): AnnotationGeometry {
  return { ...geometry, coordinates: [[...corners, corners[0]]] };
}

/**
 * Move ONE vertex of a line or polygon by `(dLng, dLat)`, leaving the rest
 * fixed — the vertex-handle drag.
 *
 * Generalizes :func:`translateLineVertex` to polygons. For a polygon the index
 * addresses a *corner* (the closing repeat is not addressable), and moving
 * corner 0 moves the repeat with it so the ring stays closed.
 */
export function translateVertex(
  geometry: AnnotationGeometry,
  index: number,
  dLng: number,
  dLat: number,
): AnnotationGeometry {
  const corners = ringCorners(geometry);
  if (corners !== null) {
    if (index < 0 || index >= corners.length) {
      return geometry;
    }
    return polygonFromCorners(
      geometry,
      corners.map((p, i) => (i === index ? translatePosition(p, dLng, dLat) : p)),
    );
  }
  return translateLineVertex(geometry, index, dLng, dLat);
}

/**
 * Insert `position` as a new vertex just after `segmentIndex` — the
 * midpoint-handle drag that turns a latent midpoint into a real corner.
 *
 * `segmentIndex` is the index of the segment's START vertex. For a polygon's
 * closing segment (last corner → first) that is the last corner's index, and
 * the new corner lands at the end of the ring, before the closing repeat.
 */
export function insertVertexAt(
  geometry: AnnotationGeometry,
  segmentIndex: number,
  position: readonly number[],
): AnnotationGeometry {
  const corners = ringCorners(geometry);
  if (corners !== null) {
    if (segmentIndex < 0 || segmentIndex >= corners.length) {
      return geometry;
    }
    const next = [...corners];
    next.splice(segmentIndex + 1, 0, [...position]);
    return polygonFromCorners(geometry, next);
  }
  if (geometry.type !== "LineString") {
    return geometry;
  }
  const coords = geometry.coordinates as number[][];
  if (segmentIndex < 0 || segmentIndex >= coords.length - 1) {
    return geometry;
  }
  const next = [...coords];
  next.splice(segmentIndex + 1, 0, [...position]);
  return { ...geometry, coordinates: next };
}

/** Fewest vertices a shape can have and still be that shape. */
const MIN_LINE_VERTICES = 2;
const MIN_POLYGON_CORNERS = 3;

/**
 * Remove the vertex at `index` — the click-to-delete on a vertex handle.
 *
 * Returns ``null`` when the removal would take the shape below its minimum (a
 * one-point line, a two-corner polygon). Refusing is deliberate: the
 * alternative — silently deleting the whole annotation — would be a surprise
 * destructive act, and annotation delete already has its own confirm-dialog
 * path. There is no undo (ADR-034 D7 defers it), so the conservative branch is
 * the right one.
 */
export function deleteVertex(
  geometry: AnnotationGeometry,
  index: number,
): AnnotationGeometry | null {
  const corners = ringCorners(geometry);
  if (corners !== null) {
    if (index < 0 || index >= corners.length || corners.length <= MIN_POLYGON_CORNERS) {
      return null;
    }
    // polygonFromCorners re-closes on the NEW first corner, so deleting corner
    // 0 drops the stale trailing repeat rather than leaving mismatched ends.
    return polygonFromCorners(
      geometry,
      corners.filter((_, i) => i !== index),
    );
  }
  if (geometry.type !== "LineString") {
    return null;
  }
  const coords = geometry.coordinates as number[][];
  if (index < 0 || index >= coords.length || coords.length <= MIN_LINE_VERTICES) {
    return null;
  }
  return { ...geometry, coordinates: coords.filter((_, i) => i !== index) };
}

/** Smallest radius a resize may produce (metres). A zero-radius circle has no
 *  edge left to grab, so it could not be resized back — unrecoverable without
 *  deleting the shape. */
const MIN_RADIUS_M = 1;

/** Mean Earth radius (metres) — matches annotation-circle-geometry. */
const EARTH_RADIUS_M = 6_371_008.8;

/**
 * The radius (metres) for a circle centred at `centre` whose edge handle has
 * been dragged to `cursor` — the circle-resize transform.
 *
 * Great-circle (haversine) distance, matching the geodesic ring the circle
 * renders as, so the edge lands under the cursor rather than near it. Clamped
 * to :data:`MIN_RADIUS_M`.
 */
export function radiusForCursor(centre: readonly number[], cursor: readonly number[]): number {
  const toRad = (deg: number): number => (deg * Math.PI) / 180;
  const dLat = toRad(cursor[1] - centre[1]);
  const dLon = toRad(cursor[0] - centre[0]);
  const lat1 = toRad(centre[1]);
  const lat2 = toRad(cursor[1]);
  const a = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  const metres = 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
  return Math.max(MIN_RADIUS_M, metres);
}
